import { useStation } from '@app/core/stations/hooks/useStation';
import { useIsExternalExportEnabled } from '@app/modules/fragments/hooks/isExternalExportEnabled';
import { contentAi, fragmentsDelivery } from '@mi-front/api-clients';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Chip } from '@mui/material';
import { useIntl } from 'react-intl';
import { ContentAiFragmentDeliveryStart } from './ContentAiFragmentDeliveryStart';
import { ContentAiFragmentInfo } from './types';

/**
 * Props for the ContentAiFragmentDeliveryInfo component
 */
interface Props {
  /** The content data containing user article information */
  content: contentAi.ContentDto;
  /** The fragment information including ID and station details */
  fragment: ContentAiFragmentInfo;
}

/**
 * ContentAiFragmentDeliveryInfo Component
 *
 * Displays and manages external delivery functionality for AI-generated content fragments.
 * This component shows either a delivery start button or a success indicator based on
 * the current delivery status and user permissions.
 *
 * The component checks three conditions to determine if delivery can be enabled:
 * 1. External export is enabled for the station (V2 only)
 * 2. The content has user-generated article data
 * 3. Delivery is not already enabled for the fragment
 *
 * @param props - Component props containing content and fragment data
 * @param props.content - The content DTO containing user article information
 * @param props.fragment - The fragment information with ID and station details
 *
 * @returns A Box component containing either:
 * - ContentAiFragmentDeliveryStart component (if delivery can be enabled)
 * - Success Chip with "Export externo activado" label (if delivery is already enabled)
 * - Nothing (if delivery cannot be enabled)
 *
 * @example
 * ```tsx
 * <ContentAiFragmentDeliveryInfo
 *   content={contentData}
 *   fragment={fragmentInfo}
 * />
 * ```
 */
export function ContentAiFragmentDeliveryInfo({ fragment, content }: Props) {
  const { $t } = useIntl();
  // Check if external export is enabled for the station (V2 only)
  const deliveryEnabledForStation = useIsExternalExportEnabled(useStation(fragment.station!.id!), {
    checkOnlyV2: true,
  });

  // Fetch the current delivery status for the fragment
  const fragmentDeliveryStatus = fragmentsDelivery.useFragmentsDeliveryAPIClientGetDeliveryStatus(
    fragment.id!,
  );

  // Determine if delivery is already enabled for this fragment
  const fragmentHasDeliveryEnabled = fragmentDeliveryStatus.data?.enabled === true;

  // Check if the content has user-generated article data
  const hasUserContent = !!content.userArticle.data;

  // Determine if delivery can be enabled based on all conditions
  const allowEnableDelivery =
    deliveryEnabledForStation && hasUserContent && !fragmentHasDeliveryEnabled;

  return (
    <Box>
      {/* Show delivery start button if delivery can be enabled */}
      {allowEnableDelivery && <ContentAiFragmentDeliveryStart fragmentId={fragment.id!} />}

      {/* Show success indicator if delivery is already enabled */}
      {fragmentHasDeliveryEnabled && (
        <Chip
          icon={<FileUploadIcon />}
          label={$t({
            id: 'contentAi.externalExportEnabled',
            defaultMessage: 'Export externo activado',
          })}
          size="small"
          color="success"
          sx={{ fontSize: 'small' }}
        />
      )}
    </Box>
  );
}
